import React, { useCallback, useState } from 'react';
import { Breadcrumb, Input, Select, Spin, Upload, UploadProps } from 'antd';
import { Link } from 'react-router-dom';

import Button from 'components/button';
import { Fence, TUploadedFence } from 'interfaces/fences';
import CardUploadedItem from '../Components/CardUploadedItem';
import { Container } from '../styles';
import { ServiceCerca } from 'services/modulos/serviceCerca';
import { toast } from 'react-toastify';
import { parseStringPromise } from 'xml2js';
import { CIDADES_ARQUIVOS_SUPESP, FILE_TYPE_SUPESP } from 'utils/reutils';
import { FenceSupespUpload } from 'interfaces/statistics';
import { ConfirmFileUploadModal } from '../Components/confirmUploadModal';
import { ServiceEstatisticas } from 'services/modulos/serviceEstatisticas';

const { Option } = Select;
type formType = {
  city: typeof CIDADES_ARQUIVOS_SUPESP[number];
  fileType: typeof FILE_TYPE_SUPESP[number];
};

export const UploadFilesSupesp = () => {
  const [loading, setLoading] = useState(false);

  const [formValues, setFormValues] = useState<formType>({
    city: 'Fortaleza',
    fileType: 'CERCA',
  });

  const uploadFormValues = useCallback((newProperties: Partial<formType>) => {
    setFormValues(prev => ({ ...prev, ...newProperties }));
  }, []);

  const [uploadedFences, setUploadedFences] = useState<FenceSupespUpload[]>([]);

  const [openUploadModal, setOpenUploadModal] = useState(false);

  const deleteItem = useCallback((index: string | number) => {
    setUploadedFences(prev => prev.filter((_, i) => i !== Number(index)));
  }, []);

  const handleSaveAllUploadedFences = useCallback(async () => {
    try {
      setOpenUploadModal(false);
      setLoading(true);

      await ServiceEstatisticas.save(uploadedFences);

      setUploadedFences([]);
      toast.success('Arquivos adicionados');
    } catch (e) {
      console.log('error', e);
    } finally {
      setLoading(false);
    }
  }, [uploadedFences]);

  const parseXmlCVLI = useCallback(
    async (xmlString: string) => {
      try {
        const result = await parseStringPromise(xmlString, {
          explicitArray: false,
        });
        const placemarks = (result?.kml?.Document?.Placemark || []) as any[];

        if (!placemarks) return [];

        const placemarkArray = Array.isArray(placemarks)
          ? placemarks
          : [placemarks];

        const parsedData: FenceSupespUpload[] = placemarkArray?.map(
          (placemark, i) => {
            let name =
              placemark.ExtendedData?.Data?.filter((d: any) =>
                d.$.name.includes('Tipo de Arma')
              )
                .map((d: any) => d.value || '')
                .filter((value: string) => value.trim() !== '')
                .join(' ')
                .trim() || `CVLI ${i + 1}`;

            let formattedCoords: number[][] = [];
            let coordinates: number[][] = [];

            if (placemark.Point) {
              coordinates = placemark.Point.coordinates
                .split('\n')
                .map((coord: any) => {
                  const [x, y] = coord.split(',').map(Number);

                  if (x && y) formattedCoords.push([x, y]);

                  return [y, x];
                })
                .filter((coord: number[][]) => coord[0] && coord[1]);
            }

            return {
              nome: name,
              descricao: '',
              points: coordinates,
              idParent: '',
              ativo: true,
              tipo: 'POINT',
              tipoArquivo: 'CVLI',
              fonte: 'SUPESP',
              cidade: formValues.city,
              alloweds: [],
            };
          }
        );

        setUploadedFences(prev => [...prev, ...parsedData]);

        return parsedData;
      } catch (error) {
        console.error('Erro ao processar XML:', error);
        return [];
      }
    },
    [formValues]
  );

  const parseXmlQUADRA = useCallback(
    async (xmlString: string) => {
      try {
        const result = await parseStringPromise(xmlString, {
          explicitArray: false,
        });
        const placemarks = (result?.kml?.Document?.Placemark || []) as any[];

        if (!placemarks) return [];

        const placemarkArray = Array.isArray(placemarks)
          ? placemarks
          : [placemarks];

        const parsedData: FenceSupespUpload[] = placemarkArray
          ?.map((placemark, i) => {
            let name =
              placemark.ExtendedData?.Data?.filter((d: any) =>
                d.$.name.includes('NM_')
              )
                .map((d: any) => d.value || '')
                .filter((value: string) => value.trim() !== '')
                .join(' ')
                .trim() || `QUADRA ${i + 1}`;

            let formattedCoords: number[][] = [];
            let coordinates: number[][] = [];

            if (placemark.LineString) {
              coordinates = placemark.LineString.coordinates
                .split('\n')
                .map((coord: any) => {
                  const [x, y] = coord.split(',').map(Number);

                  if (x && y) formattedCoords.push([x, y]);

                  return [y, x];
                })
                .filter((coord: number[][]) => coord[0] && coord[1]);
            }

            return {
              nome: name,
              descricao: '',
              points: coordinates,
              idParent: '',
              ativo: true,
              tipo: 'LINE',
              tipoArquivo: 'QUADRA',
              fonte: 'SUPESP',
              cidade: formValues.city,
              alloweds: [],
            };
          })
          .sort((f1, f2) => (f1.nome < f2.nome ? -1 : 1));

        setUploadedFences(prev => [...prev, ...parsedData]);

        return parsedData;
      } catch (error) {
        console.error('Erro ao processar XML:', error);
        return [];
      }
    },
    [formValues]
  );

  const parseXmlACE = useCallback(
    async (xmlString: string) => {
      try {
        const result = await parseStringPromise(xmlString, {
          explicitArray: false,
        });
        const placemarks = (result?.kml?.Document?.Placemark || []) as any[];

        if (!placemarks) return [];

        const placemarkArray = Array.isArray(placemarks)
          ? placemarks
          : [placemarks];

        const parsedData: FenceSupespUpload[] = placemarkArray?.map(
          (placemark, i) => {
            const name = `ACE ${i + 1}`;

            let formattedCoords: number[][] = [];
            let coordinates: number[][] = [];

            if (placemark.Polygon) {
              coordinates = placemark.Polygon.outerBoundaryIs.LinearRing.coordinates
                .split('\n')
                .map((coord: any) => {
                  const [x, y] = coord.split(',').map(Number);

                  if (x && y) formattedCoords.push([x, y]);

                  return [y, x];
                })
                .filter((coord: number[][]) => coord[0] && coord[1]);
            }

            return {
              nome: name,
              descricao: '',
              points: coordinates,
              ativo: true,
              idParent: '',
              tipo: 'POLYGON',
              tipoArquivo: 'ACE',
              fonte: 'SUPESP',
              cidade: formValues.city,
              alloweds: [],
            };
          }
        );

        setUploadedFences(prev => [...prev, ...parsedData]);

        return parsedData;
      } catch (error) {
        console.error('Erro ao processar XML:', error);
        return [];
      }
    },
    [formValues]
  );

  const parseXmlCerca = useCallback(
    async (xmlString: string) => {
      try {
        const result = await parseStringPromise(xmlString, {
          explicitArray: false,
        });
        const placemarks = (result?.kml?.Document?.Folder?.Placemark ||
          []) as any[];

        if (!placemarks) return [];

        const placemarkArray = Array.isArray(placemarks)
          ? placemarks
          : [placemarks];

        const parsedData: FenceSupespUpload[] = placemarkArray
          ?.map(placemark => {
            const fenceName = placemark.ExtendedData.SchemaData.SimpleData.find(
              (d: any) => d.$.name === 'AOP'
            )._;

            let formattedCoords: number[][] = [];
            let coordinates: number[][] = [];

            if (placemark.MultiGeometry) {
              coordinates = placemark.MultiGeometry.Polygon.outerBoundaryIs.LinearRing.coordinates
                .split(' ')
                .map((coord: any) => {
                  const [x, y] = coord.split(',').map(Number);

                  formattedCoords.push([x, y]);

                  return [y, x];
                });
            } else if (placemark.Polygon) {
              coordinates = placemark.Polygon.outerBoundaryIs.LinearRing.coordinates
                .split(' ')
                .map((coord: any) => {
                  const [x, y] = coord.split(',').map(Number);

                  formattedCoords.push([x, y]);

                  return [y, x];
                });
            }

            return {
              nome: fenceName,
              descricao: '',
              points: coordinates,
              ativo: true,
              idParent: '',
              tipo: 'POLYGON',
              tipoArquivo: 'CERCA',
              fonte: 'SUPESP',
              cidade: formValues.city,
              alloweds: [],
            };
          })
          .sort((f1, f2) => (f1.nome < f2.nome ? -1 : 1));

        setUploadedFences(prev => [...prev, ...parsedData]);

        return parsedData;
      } catch (error) {
        console.error('Erro ao processar XML:', error);
        return [];
      }
    },
    [formValues]
  );

  const uploadProps: UploadProps = {
    name: 'file',
    headers: {
      authorization: 'authorization-text',
    },
    async onChange(info) {
      if (info.file.status !== 'uploading') {
      }
      if (info.file.status === 'done') {
      } else if (info.file.status === 'error') {
        var fileReader = new FileReader();
        fileReader.onload = function(fileLoadedEvent): string {
          const textFromFileLoaded = fileLoadedEvent?.target?.result;

          if (formValues.fileType === 'CERCA') {
            parseXmlCerca(textFromFileLoaded + '');
          } else if (formValues.fileType === 'ACE') {
            parseXmlACE(textFromFileLoaded + '');
          } else if (formValues.fileType === 'QUADRA') {
            parseXmlQUADRA(textFromFileLoaded + '');
          } else if (formValues.fileType === 'CVLI') {
            parseXmlCVLI(textFromFileLoaded + '');
          }

          return String(textFromFileLoaded);
        };

        fileReader.readAsText(info.file.originFileObj || new Blob(), 'UTF-8');
      } else if (info.file.status === 'removed') {
        // handleRemoveUploadedFile(info.file.name || '');
      }
    },
  };

  return (
    <>
      <Container>
        <Breadcrumb style={{ padding: '10px' }}>
          <Breadcrumb.Item>
            {/* @ts-expect-error Server Component */}
            <Link to="/">Página Inicial</Link>
          </Breadcrumb.Item>

          <Breadcrumb.Item>
            {/* @ts-expect-error Server Component */}
            <Link to="/estatisticas-supesp">Estatísticas</Link>
          </Breadcrumb.Item>

          <Breadcrumb.Item>Upload</Breadcrumb.Item>
        </Breadcrumb>
        <Spin spinning={loading}>
          <div className="body" style={{ textAlign: 'center' }}>
            <div className="title">
              <h1>Upload de Arquivos Estatísticos</h1>
            </div>
            <div className="formContainer">
              <Select
                value={formValues.fileType}
                onChange={e => uploadFormValues({ fileType: e })}
                showSearch
                style={{ width: 200 }}
                virtual={false}
                notFoundContent={null}
                getPopupContainer={trigger => trigger.parentElement}
                placeholder="Informe o tipo de arquivo."
                filterOption={(input, option) => {
                  return Boolean(
                    option?.children &&
                      String(option.children || '')
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              >
                {FILE_TYPE_SUPESP.map(item => (
                  <Option key={item} value={item}>
                    {item}
                  </Option>
                ))}
              </Select>

              <Select
                value={formValues.city}
                onChange={e => uploadFormValues({ city: e })}
                showSearch
                style={{ width: 200 }}
                virtual={false}
                notFoundContent={null}
                getPopupContainer={trigger => trigger.parentElement}
                placeholder="Informe a cidade."
                filterOption={(input, option) => {
                  return Boolean(
                    option?.children &&
                      String(option.children || '')
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              >
                {CIDADES_ARQUIVOS_SUPESP.map(city => (
                  <Option key={city} value={city}>
                    {city}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="buttonsDivUpload">
              <Button
                disabled={!uploadedFences.length}
                type="button"
                buttonStyle="btn--primary"
                onClick={() => setOpenUploadModal(true)}
              >
                Salvar
              </Button>
              <Upload {...uploadProps}>
                <Button
                  type="button"
                  buttonStyle="btn--link"
                  disabled={Object.keys(formValues).some(
                    key => !formValues[key as keyof formType]
                  )}
                >
                  Upload
                </Button>
              </Upload>
            </div>
            {/* <Search className="inputButton" /> */}
            <div className="cardList">
              {uploadedFences?.map((f, i) => {
                return (
                  <CardUploadedItem
                    id={i + ''}
                    key={i}
                    nome={`${f.nome}`}
                    feature={f}
                    deleteItem={deleteItem}
                    system={false}
                    tipo={f.tipoArquivo || 'CERCA'}
                  />
                );
              })}
            </div>
          </div>
        </Spin>
      </Container>
      <ConfirmFileUploadModal
        open={openUploadModal}
        setModalVisible={setOpenUploadModal}
        handleConfirmModal={handleSaveAllUploadedFences}
      />
    </>
  );
};
